// Console command catalog.
//
// Client commands are generated from the settings registry (one source of
// truth): every scalar setting the player's role can see becomes a command,
// with the registry id always accepted and a curated short name where it is
// useful. Server rules/modes/binds are deliberately NOT here — see the
// generated server scope in main.ts (rules/modes are authoritative, binds use
// `bind`/`unbind`).
//
// The module is pure (no DOM/main state) so the help renderer, the future
// command popup and tests can share it.

import type { Role } from "../admin";
import { canSeeSetting } from "../settings/access";
import { accessFor, loadCachedGlobalDefaults } from "../settings/defaults";
import {
  SETTINGS,
  categoryLabel,
  formatDefault,
  formatSettingValue,
  getSettingValue,
  type SettingDef,
} from "../settings/registry";

/**
 * Curated short console names. The first alias for an id becomes the primary
 * name; the id itself is always accepted too.
 */
const ALIASES: Record<string, string> = {
  // Video
  fov: "video.fov",
  panini: "video.panini",
  texture: "video.textureMode",
  texmode: "video.textureMode",
  texfilter: "video.textureMode",

  // Effects
  truelightning: "video.effects.truelightning",
  tl: "video.effects.truelightning",
  trail: "video.effects.trailSeconds",
  trailwidth: "video.effects.trailWidth",
  trailglow: "video.effects.trailIntensity",
  trailselfmode: "video.effects.trailSelfMode",
  trailselfcolor: "video.effects.trailSelfColor",
  trailselfteamcolor: "video.effects.trailSelfTeamColor",
  trailenemymode: "video.effects.trailEnemyMode",
  trailenemycolor: "video.effects.trailEnemyColor",
  trailenemyteamcolor: "video.effects.trailEnemyTeamColor",
  outlinesize: "video.enemy.outlineSize",
  outlinebrightness: "video.enemy.outlineBrightness",
  halosize: "video.enemy.haloSize",
  halobrightness: "video.enemy.haloBrightness",
  explosioncolor: "video.vfx.explosion",
  sparkscolor: "video.vfx.sparks",
  muzzlecolor: "video.vfx.muzzle",
  smokecolor: "video.vfx.smoke",

  // Hit feedback
  hitmarker: "video.hit.hitmarker",
  hitmarkerstyle: "video.hit.hitmarkerStyle",
  hitmarkersize: "video.hit.hitmarkerSize",
  hitmarkerthickness: "video.hit.hitmarkerThickness",
  hitmarkercolor: "video.hit.hitmarkerColor",
  hitmarkerkillcolor: "video.hit.hitmarkerKillColor",
  hitmarkerduration: "video.hit.hitmarkerDuration",
  hitmarkeropacity: "video.hit.hitmarkerOpacity",
  damagenumbers: "video.hit.damageNumbers",
  damagenumbersize: "video.hit.damageNumberSize",
  damagenumbercolor: "video.hit.damageNumberColor",
  damagenumberkillcolor: "video.hit.damageNumberKillColor",
  damagenumberduration: "video.hit.damageNumberDuration",
  damagenumberrise: "video.hit.damageNumberRise",
  damagenumberopacity: "video.hit.damageNumberOpacity",

  // Audio (sounds.* fall back to their ids)
  hitvolume: "audio.hitSoundVolume",
  hitsoundvolume: "audio.hitSoundVolume",
  volume: "audio.volume",
  vol: "audio.volume",

  // Controls / weapons defaults
  sens: "controls.sensitivity",
  sensitivity: "controls.sensitivity",
  yawmult: "weapons.defaults.yawMultiplier",
  pitchmult: "weapons.defaults.pitchMultiplier",

  // HUD / cgaz
  cgazopacity: "hud.cgaz.opacity",
  cgazheight: "hud.cgaz.height",
  cgazwidth: "hud.cgaz.width",
  cgazbg: "hud.cgaz.bgOpacity",
  cgazbackground: "hud.cgaz.bgOpacity",
  cgazshadow: "hud.cgaz.shadow",
  cgazborder: "hud.cgaz.border",
  cgazborderwidth: "hud.cgaz.borderWidth",
  cgazbordercolor: "hud.cgaz.borderColor",
  cgazborderopacity: "hud.cgaz.borderOpacity",
  cgazdead: "hud.cgaz.colors.dead",
  cgazpartial: "hud.cgaz.colors.partial",
  cgazfast: "hud.cgaz.colors.fast",
  cgazturn: "hud.cgaz.colors.turn",
  cgazdecel: "hud.cgaz.colors.decel",
  cgazdeadalpha: "hud.cgaz.alphas.dead",
  cgazpartialalpha: "hud.cgaz.alphas.partial",
  cgazfastalpha: "hud.cgaz.alphas.fast",
  cgazturnalpha: "hud.cgaz.alphas.turn",
  cgazdecelalpha: "hud.cgaz.alphas.decel",

  // Advanced / debug (runtime flags are session-only)
  snap: "advanced.downstreamHz",
  downstream: "advanced.downstreamHz",
  wireframe: "advanced.debug.wireframe",
  pvs: "advanced.debug.pvs",
  frustum: "advanced.debug.frustum",
  rawmouse: "advanced.debug.rawmouse",
  vfxtrails: "advanced.debug.vfxtrails",
  vfxcollide: "advanced.debug.vfxcollide",
  bloom: "advanced.debug.bloom",
  netgraph: "advanced.debug.netgraph",
  profiler: "advanced.debug.profiler",
  interp: "advanced.debug.interp",
  smooth: "advanced.debug.smooth",
  lag: "advanced.debug.lag",
  jitter: "advanced.debug.jitter",
  loss: "advanced.debug.loss",
  fpscap: "advanced.debug.fpscap",
  vfxamount: "advanced.debug.vfxamount",
  smokedensity: "advanced.debug.smokedensity",
  smokeopacity: "advanced.debug.smokeopacity",
  vfxsize: "advanced.debug.vfxsize",
  bloomintensity: "advanced.debug.bloomintensity",
  bloomthreshold: "advanced.debug.bloomthreshold",
};

/** Aliases grouped by setting id, preserving declaration order. */
const ALIASES_BY_ID = new Map<string, string[]>();
for (const [alias, id] of Object.entries(ALIASES)) {
  const list = ALIASES_BY_ID.get(id) ?? [];
  list.push(alias);
  ALIASES_BY_ID.set(id, list);
}

if (import.meta.env.DEV) {
  const ids = new Set(SETTINGS.map((def) => def.id));
  for (const [alias, id] of Object.entries(ALIASES)) {
    if (!ids.has(id)) console.error(`[console] alias "${alias}" -> unknown setting ${id}`);
    if (ids.has(alias)) console.error(`[console] alias "${alias}" collides with a setting id`);
  }
}

/** Categories whose entries are server-authoritative editors, not client cvars. */
const SERVER_CATEGORIES = new Set(["rules", "game-modes"]);

export interface ClientCommand {
  def: SettingDef;
  /** Primary name (curated alias, else the registry id). */
  name: string;
  /** Other accepted names (the id and the remaining aliases). */
  aliases: string[];
  /** Top-level registry category (`video`, `audio`, …). */
  category: string;
  /** Human category path (`Video / Effects`). */
  pathLabel: string;
  /** Runtime debug flag: applied live, not persisted across reloads. */
  runtime: boolean;
}

/** Every client setting the role may see, as console commands. */
export function clientCommands(role: Role, doc = loadCachedGlobalDefaults()): ClientCommand[] {
  const out: ClientCommand[] = [];
  for (const def of SETTINGS) {
    if (SERVER_CATEGORIES.has(def.category[0])) continue;
    if (def.kind === "group" || def.kind === "bind") continue;
    if (!def.path && !def.get && !def.runtimeKey) continue;
    if (!canSeeSetting(accessFor(def, doc), role)) continue;
    const curated = ALIASES_BY_ID.get(def.id) ?? [];
    const name = curated[0] ?? def.id;
    const aliases = [def.id, ...curated.slice(1)].filter((a) => a !== name);
    out.push({
      def,
      name,
      aliases,
      category: def.category[0],
      pathLabel: categoryLabel(def.category),
      runtime: Boolean(def.runtimeKey),
    });
  }
  return out;
}

/** Case-insensitive lookup by primary name, alias or registry id. */
export function findClientCommand(
  commands: readonly ClientCommand[],
  name: string,
): ClientCommand | undefined {
  const q = name.trim().toLowerCase();
  if (!q) return undefined;
  return commands.find(
    (c) =>
      c.name.toLowerCase() === q ||
      c.def.id.toLowerCase() === q ||
      c.aliases.some((a) => a.toLowerCase() === q),
  );
}

/** Current value of a client command (runtime flags read the live debug state). */
export function readCommand(
  command: ClientCommand,
  settings: unknown,
  runtimeState: Record<string, number | boolean>,
): unknown {
  if (command.def.runtimeKey) return runtimeState[command.def.runtimeKey];
  return getSettingValue(command.def, settings);
}

/** Usage line for a client command. */
export function usageFor(command: ClientCommand): string {
  const { def, name } = command;
  switch (def.kind) {
    case "toggle":
      return `${name} <on|off>`;
    case "number": {
      const min = def.min ?? "?";
      const max = def.max ?? "?";
      const step = def.step ? `, step ${def.step}` : "";
      const unit = def.unit ? ` ${def.unit}` : "";
      return `${name} <${min}..${max}>${step}${unit}`;
    }
    case "select":
      return `${name} <${(def.options ?? []).map((o) => o.value).join("|")}>`;
    case "color":
      return `${name} <#rrggbb>`;
    case "text":
      return `${name} <text>`;
    default:
      return `${name} <value>`;
  }
}

export type ParsedValue =
  | { ok: true; value: unknown; relative?: boolean }
  | { ok: false; error: string };

/** Parse console arguments for a client setting, clamping numbers to range. */
export function parseCommandValue(
  command: ClientCommand,
  args: readonly string[],
  current: unknown,
): ParsedValue {
  const { def } = command;
  const raw = args.join(" ").trim();
  if (!raw) return { ok: false, error: `usage: ${usageFor(command)}` };

  if (def.kind === "toggle") {
    const v = raw.toLowerCase();
    if (["on", "true", "1", "yes", "enable", "enabled"].includes(v)) {
      return { ok: true, value: true };
    }
    if (["off", "false", "0", "no", "disable", "disabled"].includes(v)) {
      return { ok: true, value: false };
    }
    return { ok: false, error: `usage: ${usageFor(command)}` };
  }

  if (def.kind === "number") {
    const n = Number(raw);
    if (!Number.isFinite(n)) return { ok: false, error: `not a number: ${raw}` };
    // `+5` / `-0.5` are relative to the current value (nice for sliders).
    const relative = (raw.startsWith("+") || raw.startsWith("-")) && raw.length > 1;
    const base = relative && typeof current === "number" ? current : 0;
    let value = relative ? base + n : n;
    const min = def.min ?? Number.NEGATIVE_INFINITY;
    const max = def.max ?? Number.POSITIVE_INFINITY;
    value = Math.min(max, Math.max(min, value));
    if (def.step && def.step > 0) value = Math.round(value / def.step) * def.step;
    // Trim float noise from the step rounding (0.30000000000000004).
    value = Math.round(value * 1e6) / 1e6;
    return { ok: true, value, relative };
  }

  if (def.kind === "select") {
    const v = raw.toLowerCase();
    const match = (def.options ?? []).find(
      (o) => o.value.toLowerCase() === v || o.label.toLowerCase() === v,
    );
    if (!match) {
      return {
        ok: false,
        error: `must be one of: ${(def.options ?? []).map((o) => o.value).join(", ")}`,
      };
    }
    // Numeric selects keep their number type (the registry shipped type).
    const value = typeof def.shipped === "number" ? Number(match.value) : match.value;
    return { ok: true, value };
  }

  if (def.kind === "color") {
    if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) {
      return { ok: false, error: "expected a colour like #ff7a2f" };
    }
    return { ok: true, value: raw.toLowerCase() };
  }

  // text
  return { ok: true, value: raw };
}

/** `value, default value` summary for a command (the bare-command line). */
export function commandSummary(command: ClientCommand, value: unknown): string {
  const def = command.def;
  const shown = def.kind === "group" ? "—" : formatSettingValue(def, value);
  return `${command.name} = ${shown}, default ${command.name} = ${formatDefault(def)}`;
}

/** All short + long names for a command, for help and suggestions. */
export function commandNames(command: ClientCommand): string[] {
  return [command.name, ...command.aliases];
}

export { formatSettingValue };
